/**
 * Provides quadtree utility functions
 * @class QuadtreeUtils
 */
export class QuadtreeUtils {
    /**
     * @method QuadKeyToNormalizedCoord
     * @static
     * @param {string} quadKey
     * @return {number[]}
     */
    static QuadKeyToNormalizedCoord(quadKey) {
        const coords = [];
        const nLevelOfDetail = quadKey.length;
        let x = 0;
        let y = 0;
        let scale = 1.0;
        for (let i = 0; i < nLevelOfDetail; i++) {
            scale /= 2.0;
            switch (quadKey[i]) {
                case "0":
                    y += scale;
                    break;
                case "1":
                    x += scale;
                    y += scale;
                    break;
                case "2":
                    break;
                case "3":
                    x += scale;
                    break;
                default:
                    console.log("Wrong quadKey: " + quadKey);
            }
        }
        coords[0] = x; //x0
        coords[1] = 1 - (y + scale); //y0
        coords[2] = x + scale; //x1
        coords[3] = 1 - y; //y1
        return coords;
    }
    /**
     * @method NormalizedCoordToQuadKey
     * @static
     * @param {number} nx - the normalized x coordinate
     * @param {number} ny - the normalized y coordinate
     * @param {number} lod - level of detail...
     * @return {string}
     */
    static NormalizedCoordToQuadKey(nx, ny, lod) {
        let quadcode = "";
        for (let i = 1; i <= lod; i++) {
            const tresh = 1 / Math.pow(2, i);
            if (nx < tresh && ny < tresh)
                quadcode = quadcode + "0";
            if (nx >= tresh && ny < tresh) {
                quadcode = quadcode + "1";
                nx = nx - tresh;
            }
            if (nx < tresh && ny >= tresh) {
                quadcode = quadcode + "2";
                ny = ny - tresh;
            }
            if (nx >= tresh && ny >= tresh) {
                quadcode = quadcode + "3";
                nx = nx - tresh;
                ny = ny - tresh;
            }
        }
        return quadcode;
    }
    /**
     * Returns the quadkey of the parent quad.
     * @method GetParent
     * @static
     * @param {string} quadKey the quadKey as string for example "0123"
     * @return {string}
     */
    static GetParent(quadKey) {
        if (quadKey.length < 2)
            return "";
        return quadKey.slice(0, quadKey.length - 1);
    }
    /**
     * Returns the quadkey of the parent quad.
     * @method GetChildrenAt
     * @static
     * @param {string} quadKey the quadKey as string for example "0123"
     * @param   {number} lod desired LOD level has to bi greater that  input key!!
     */
    static GetChildrenAt(quadKey, lod) {
        let children = [];
        const lodDelta = lod - quadKey.length;
        // same lod level
        if (lodDelta < 1) {
            return [quadKey];
        }
        else {
            for (let i = 0; i < 4; i++) {
                children = children.concat(QuadtreeUtils.GetChildrenAt(quadKey + i, lod));
            }
        }
        return children;
    }
    /**
     * Calculates the tile coord of the specific quadkey, origin is set to left bottom corner.
     * returns result.x, result.y -> Tile coordinates
     *         result.lod -> level of detail
     * @method QuadKeyToTileCoord
     * @static
     * @param {string} quadcode the quadKey as string for example "0123"
     * @return {Object}
     *
     */
    static QuadKeyToTileCoord(quadcode) {
        const result = {
            x: 0,
            y: 0,
            lod: quadcode.length,
        };
        let mask;
        for (let i = result.lod; i > 0; i--) {
            mask = 1 << (i - 1);
            switch (quadcode[result.lod - i]) {
                case "0":
                    break;
                case "1":
                    result.x |= mask;
                    break;
                case "2":
                    result.y |= mask;
                    break;
                case "3":
                    result.x |= mask;
                    result.y |= mask;
                    break;
            }
        }
        return result;
    }
    static TileCoordToQuadKey(x, y, z) {
        const quadKey = [];
        for (let i = z; i > 0; i--) {
            let digit = 0;
            const mask = 1 << (i - 1);
            if ((x & mask) != 0)
                digit++;
            if ((y & mask) != 0) {
                digit++;
                digit++;
            }
            quadKey.push(digit);
        }
        return quadKey.join("");
    }
    /**
     * Returns a list with all quadcodes within and overlapping the bounding box.
     * @param {number} ul_x upper-left-corner_x
     * @param {number} ul_y upper-left-corner_y
     * @param {number} br_x bottom-right-corner_x
     * @param {number} br_y bottom-right-corner_y
     * @param {number} lod the level of detail
     * @return {string[]}
     */
    static GetQuadKeysWithinNormalizedBoundingBox(ul_x, ul_y, br_x, br_y, lod) {
        const quadlist = [];
        const q1 = QuadtreeUtils.NormalizedCoordToQuadKey(ul_x, ul_y, lod);
        const ts = 1 / Math.pow(2, lod);
        const c1 = QuadtreeUtils.QuadKeyToNormalizedCoord(q1);
        let x = c1[0];
        let y = c1[1];
        while (x < br_x) {
            y = c1[1];
            while (y < br_y) {
                const q3 = QuadtreeUtils.NormalizedCoordToQuadKey(x, y, lod);
                quadlist.push(q3);
                y += ts;
            }
            x += ts;
        }
        return quadlist;
    }
}
//# sourceMappingURL=quadtreeutils.js.map